// IKRouteMap.jsx
import { useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, Marker, Source } from 'react-map-gl/mapbox';
import polyline from '@mapbox/polyline';
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import HomeIcon from '@mui/icons-material/Home';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PlaceIcon from '@mui/icons-material/Place';
import 'mapbox-gl/dist/mapbox-gl.css';

const segmentPalette = ['#2563eb', '#0f766e', '#ea580c', '#7c3aed', '#db2777', '#0891b2'];

const routeLayer = {
  id: 'ik-route-line-segments',
  type: 'line',
  paint: {
    'line-color': ['get', 'color'],
    'line-width': 5,
    'line-opacity': 0.9,
  },
};

const km = (m) => Number((m / 1000).toFixed(2));

function toRadians(value) {
  return (Number(value || 0) * Math.PI) / 180;
}

function getDistanceBetweenCoordinates([lng1, lat1], [lng2, lat2]) {
  const earthRadiusMeters = 6371000;
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a = (
    Math.sin(dLat / 2) ** 2
    + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) ** 2
  );
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusMeters * c;
}

function getLineDistanceMeters(coordinates = []) {
  if (!Array.isArray(coordinates) || coordinates.length < 2) return 0;

  let total = 0;
  for (let i = 1; i < coordinates.length; i++) {
    total += getDistanceBetweenCoordinates(coordinates[i - 1], coordinates[i]);
  }
  return total;
}

const pointTypeIcons = {
  home: HomeIcon,
  client: CorporateFareIcon,
  fournisseur: LocalShippingIcon,
  partenaire: HandshakeIcon,
  manual: PlaceIcon,
  organization: CorporateFareIcon,
};

function getPointType(point) {
  if (point?.selectionType) return point.selectionType;
  if (point?.sourceType) return point.sourceType;
  return 'manual';
}

function getPointDisplayLabel(point, index) {
  const pointType = getPointType(point);
  const organizationName = String(point?.organizationName || '').trim();
  const siteName = String(point?.siteName || '').trim();
  const label = String(point?.label || '').trim();
  const normalizedOrganizationName = organizationName.toLowerCase();
  const normalizedLabel = label.toLowerCase();

  if (pointType === 'home') {
    return label || 'Domicile';
  }

  if (['client', 'fournisseur', 'partenaire', 'organization'].includes(pointType) && organizationName) {
    const derivedSiteName = siteName
      || (label && normalizedLabel !== normalizedOrganizationName ? label : '');
    return `${organizationName} - ${derivedSiteName || 'Siege'}`;
  }

  if (['client', 'fournisseur', 'partenaire', 'organization'].includes(pointType) && label) {
    return label.includes(' - ') ? label : `${label} - ${siteName || 'Siege'}`;
  }

  return label || `Point ${index + 1}`;
}

function getNearestCoordinateIndex(routeCoordinates = [], pointCoordinates, fromIndex = 0) {
  if (!Array.isArray(routeCoordinates) || routeCoordinates.length === 0 || !pointCoordinates) return fromIndex;

  let nearestIndex = Math.max(0, fromIndex);
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (let i = Math.max(0, fromIndex); i < routeCoordinates.length; i++) {
    const [lng, lat] = routeCoordinates[i] || [];
    const distance = Math.hypot(
      Number(lng || 0) - Number(pointCoordinates.lng || 0),
      Number(lat || 0) - Number(pointCoordinates.lat || 0)
    );

    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = i;
    }
  }

  return nearestIndex;
}

export default function IKRouteMap({ geometry, points = [], height = 360, distanceKm = null }) {
  const mapRef = useRef(null);

  const routeGeoJSON = useMemo(() => {
    if (!geometry) return null;
    try {
      const coords = polyline.decode(geometry).map(([lat, lng]) => [lng, lat]);
      return {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            properties: { color: segmentPalette[0], order: 1 },
            geometry: { type: 'LineString', coordinates: coords },
          },
        ],
      };
    } catch {
      return null;
    }
  }, [geometry]);

  const validPoints = useMemo(
    () => (points || []).filter((p) => p?.coordinates?.lng != null && p?.coordinates?.lat != null),
    [points]
  );

  const segmentGeoJSON = useMemo(() => {
    const coordinates = routeGeoJSON?.features?.[0]?.geometry?.coordinates || [];
    if (!coordinates.length || validPoints.length < 2) return null;

    const features = [];
    let previousRouteIndex = 0;

    for (let i = 0; i < validPoints.length - 1; i++) {
      const startPoint = validPoints[i];
      const endPoint = validPoints[i + 1];
      const startRouteIndex = i === 0
        ? 0
        : getNearestCoordinateIndex(coordinates, startPoint.coordinates, previousRouteIndex);
      const endRouteIndex = getNearestCoordinateIndex(coordinates, endPoint.coordinates, startRouteIndex);
      const segmentCoordinates = coordinates.slice(startRouteIndex, endRouteIndex + 1);

      if (segmentCoordinates.length >= 2) {
        features.push({
          type: 'Feature',
          properties: {
            color: segmentPalette[i % segmentPalette.length],
            order: i + 1,
          },
          geometry: {
            type: 'LineString',
            coordinates: segmentCoordinates,
          },
        });
      }

      previousRouteIndex = endRouteIndex;
    }

    return features.length
      ? { type: 'FeatureCollection', features }
      : routeGeoJSON;
  }, [routeGeoJSON, validPoints]);

  const segmentMarkers = useMemo(() => {
    const features = segmentGeoJSON?.features || [];
    return features
      .map((feature) => {
        const segmentCoordinates = feature?.geometry?.coordinates || [];
        if (!segmentCoordinates.length) return null;

        const midpoint = segmentCoordinates[Math.floor(segmentCoordinates.length / 2)];
        if (!midpoint) return null;

        return {
          lng: midpoint[0],
          lat: midpoint[1],
          order: feature?.properties?.order,
          color: feature?.properties?.color || segmentPalette[0],
        };
      })
      .filter(Boolean);
  }, [segmentGeoJSON]);

  const computedLegMeters = useMemo(() => {
    const features = segmentGeoJSON?.features || [];
    if (!features.length || validPoints.length < 2) return [];

    return features.map((feature) => getLineDistanceMeters(feature?.geometry?.coordinates || []));
  }, [segmentGeoJSON, validPoints]);

  const legs = useMemo(() => {
    if (!computedLegMeters.length || validPoints.length < 2) return [];

    const computedTotalMeters = computedLegMeters.reduce((sum, value) => sum + Number(value || 0), 0);
    const overrideKm = Number(distanceKm);
    const hasOverride = Number.isFinite(overrideKm) && overrideKm > 0 && computedTotalMeters > 0;
    const scaleRatio = hasOverride ? ((overrideKm * 1000) / computedTotalMeters) : 1;

    const scaledRawKm = computedLegMeters.map((meters) => (meters * scaleRatio) / 1000);
    const roundedKm = scaledRawKm.map((value) => Number(value.toFixed(2)));

    if (hasOverride && roundedKm.length > 0) {
      const roundedSum = Number(roundedKm.reduce((sum, value) => sum + value, 0).toFixed(2));
      const delta = Number((overrideKm - roundedSum).toFixed(2));
      roundedKm[roundedKm.length - 1] = Number((roundedKm[roundedKm.length - 1] + delta).toFixed(2));
    }

    return roundedKm.map((distanceKm, i) => ({
      idx: i + 1,
      from: getPointDisplayLabel(validPoints[i], i),
      to: getPointDisplayLabel(validPoints[i + 1], i + 1),
      distanceKm,
    }));
  }, [computedLegMeters, distanceKm, validPoints]);

  const totalDistanceKm = useMemo(() => {
    if (!legs.length) return null;
    return Number(legs.reduce((sum, leg) => sum + Number(leg.distanceKm || 0), 0).toFixed(2));
  }, [legs]);

  const initialViewState = useMemo(() => {
    const first = validPoints?.[0]?.coordinates;
    if (first) return { longitude: first.lng, latitude: first.lat, zoom: 9 };
    return { longitude: 2.2137, latitude: 46.2276, zoom: 5 };
  }, [validPoints]);

  const zoomToPoint = (p) => {
    if (!p?.coordinates || !mapRef.current) return;
    mapRef.current.flyTo({
      center: [p.coordinates.lng, p.coordinates.lat],
      zoom: 14,
      speed: 1.05,
      curve: 1.42,
      essential: true,
    });
  };

  const fitAllPoints = () => {
    if (!mapRef.current || validPoints.length === 0) return;
    if (validPoints.length === 1) return zoomToPoint(validPoints[0]);

    const lons = validPoints.map((p) => p.coordinates.lng);
    const lats = validPoints.map((p) => p.coordinates.lat);
    const bounds = [
      [Math.min(...lons), Math.min(...lats)],
      [Math.max(...lons), Math.max(...lats)],
    ];
    mapRef.current.fitBounds(bounds, { padding: 60, duration: 700 });
  };

  useEffect(() => {
    if (!validPoints.length) return undefined;

    const timeoutId = window.setTimeout(() => {
      fitAllPoints();
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [geometry, validPoints]);

  return (
    <Stack spacing={1.5}>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
          borderColor: 'divider',
        }}
      >
        <Map
          ref={mapRef}
          mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
          initialViewState={initialViewState}
          onLoad={fitAllPoints}
          style={{ width: '100%', height }}
          mapStyle="mapbox://styles/mapbox/streets-v12"
        >
          {segmentGeoJSON && (
            <Source id="ik-route-source" type="geojson" data={segmentGeoJSON}>
              <Layer {...routeLayer} />
            </Source>
          )}

          {validPoints.map((p, i) => (
            <Marker key={`${p.label || 'p'}-${i}`} longitude={p.coordinates.lng} latitude={p.coordinates.lat}>
              <Tooltip title={getPointDisplayLabel(p, i)}>
                {(() => {
                  const PointIcon = pointTypeIcons[getPointType(p)] || PlaceIcon;

                  return (
                    <Box
                      onClick={() => zoomToPoint(p)}
                      sx={{
                        cursor: 'pointer',
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        bgcolor: i === 0 ? '#16a34a' : i === validPoints.length - 1 ? '#dc2626' : '#2563eb',
                        border: '2px solid #fff',
                        boxShadow: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                      }}
                    >
                      <PointIcon sx={{ fontSize: 18 }} />
                    </Box>
                  );
                })()}
              </Tooltip>
            </Marker>
          ))}

          {segmentMarkers.map((segmentMarker) => (
            <Marker
              key={`segment-order-${segmentMarker.order}`}
              longitude={segmentMarker.lng}
              latitude={segmentMarker.lat}
            >
              <Box
                sx={{
                  minWidth: 24,
                  height: 24,
                  borderRadius: 999,
                  bgcolor: segmentMarker.color,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 800,
                  border: '2px solid #fff',
                  boxShadow: 2,
                }}
              >
                {segmentMarker.order}
              </Box>
            </Marker>
          ))}
        </Map>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            right: 8,
            zIndex: 10,
            justifyContent: 'space-between',
            pointerEvents: 'none',
          }}
        >
          <Stack direction="row" spacing={1} sx={{ pointerEvents: 'auto', maxWidth: '78%', overflowX: 'auto' }}>
            {validPoints.map((p, i) => (
              (() => {
                const PointIcon = pointTypeIcons[getPointType(p)] || PlaceIcon;
                return (
              <Button
                key={`${p.label || 'pbtn'}-${i}`}
                size="small"
                variant="contained"
                color={i === 0 ? 'success' : i === validPoints.length - 1 ? 'error' : 'primary'}
                startIcon={<PointIcon />}
                onClick={() => zoomToPoint(p)}
                sx={{ whiteSpace: 'nowrap', minWidth: 'auto', px: 1.25 }}
              >
                {getPointDisplayLabel(p, i)}
              </Button>
                );
              })()
            ))}
          </Stack>

          <Stack direction="row" spacing={1} sx={{ pointerEvents: 'auto' }}>
            <Button size="small" variant="contained" color="inherit" startIcon={<FitScreenIcon />} onClick={fitAllPoints}>
              Fit
            </Button>
            {validPoints[0] && (
              <Button
                size="small"
                variant="contained"
                color="inherit"
                startIcon={<MyLocationIcon />}
                onClick={() => zoomToPoint(validPoints[0])}
              >
                Départ
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AltRouteIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2">Détail du trajet</Typography>
          </Stack>
          <Chip
            size="small"
            color="primary"
            variant="outlined"
            label={`Total: ${totalDistanceKm != null ? `${totalDistanceKm} km` : '—'}`}
          />
        </Stack>

        <Divider sx={{ mb: 1 }} />

        <Stack spacing={0.75}>
          {!legs.length ? (
            <Typography variant="caption" color="text.secondary">
              Aucun segment disponible.
            </Typography>
          ) : (
            legs.map((leg) => (
              <Stack key={leg.idx} direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center" sx={{ maxWidth: '75%' }}>
                  {(() => {
                    const fromPoint = validPoints[leg.idx - 1];
                    const toPoint = validPoints[leg.idx];
                    const FromIcon = pointTypeIcons[getPointType(fromPoint)] || PlaceIcon;
                    const ToIcon = pointTypeIcons[getPointType(toPoint)] || PlaceIcon;

                    return (
                      <>
                        <FromIcon fontSize="small" color="action" />
                        <Typography variant="body2" noWrap>
                          {leg.from} → {leg.to}
                        </Typography>
                        <ToIcon fontSize="small" color="action" />
                      </>
                    );
                  })()}
                </Stack>
                <Chip size="small" label={`${leg.distanceKm} km`} />
              </Stack>
            ))
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
